import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  CreateInvestmentManagerNotePayload,
  CreatePrivateInvestmentManagerPayload,
  GenerateInvestmentManagerReportPayload,
  InvestmentManagerNote,
  InvestmentManagerProductRelation,
  InvestmentManagerAnalytics,
  InvestmentManagerCareer,
  InvestmentManagerEvaluation,
  InvestmentManagerReportDetail,
  InvestmentManagerReportSummary,
  InvestmentManagerPortrait,
  InvestmentManagerResearchOutput,
  MarketInvestmentManagerDetail,
  MarketInvestmentManagerListParams,
  MarketInvestmentManagerListResponse,
  PrivateInvestmentManagerDetail,
  PrivateInvestmentManagerListParams,
  PrivateInvestmentManagerListResponse,
  PrivateInvestmentManagerStage,
  UpdateInvestmentManagerReportPayload,
} from './types/investment-manager';

const request = createAppRequest();

export const getMarketInvestmentManagers = (
  params?: MarketInvestmentManagerListParams,
  options?: ApiRequestOptions,
) =>
  request<MarketInvestmentManagerListResponse>('/bff/market-data/investment-managers', {
    method: 'GET',
    params,
    ...(options || {}),
  });

export const getMarketInvestmentManager = (id: number, options?: ApiRequestOptions) =>
  request<MarketInvestmentManagerDetail>(`/bff/market-data/investment-managers/${id}`, {
    method: 'GET',
    ...(options || {}),
  });

export const compareMarketInvestmentManagers = (ids: number[], options?: ApiRequestOptions) =>
  request<MarketInvestmentManagerDetail[]>('/bff/market-data/investment-managers/compare', {
    method: 'GET',
    params: { ids: ids.join(',') },
    ...(options || {}),
  });

export const getPrivateInvestmentManagers = (
  params?: PrivateInvestmentManagerListParams,
  options?: ApiRequestOptions,
) =>
  request<PrivateInvestmentManagerListResponse>('/bff/fams/investment-managers', {
    method: 'GET',
    params,
    ...(options || {}),
  });

export const createPrivateInvestmentManager = (
  data: CreatePrivateInvestmentManagerPayload,
  options?: ApiRequestOptions,
) =>
  request<PrivateInvestmentManagerDetail>('/bff/fams/investment-managers', {
    method: 'POST',
    data,
    ...(options || {}),
  });

export const getPrivateInvestmentManager = (id: number, options?: ApiRequestOptions) =>
  request<PrivateInvestmentManagerDetail>(`/bff/fams/investment-managers/${id}`, {
    method: 'GET',
    ...(options || {}),
  });

export const updatePrivateInvestmentManager = (
  id: number,
  data: Omit<CreatePrivateInvestmentManagerPayload, 'marketManagerId' | 'stage'>,
  options?: ApiRequestOptions,
) =>
  request<PrivateInvestmentManagerDetail>(`/bff/fams/investment-managers/${id}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });

export const updatePrivateInvestmentManagerStage = (
  id: number,
  stage: PrivateInvestmentManagerStage,
  options?: ApiRequestOptions,
) =>
  request<PrivateInvestmentManagerDetail>(`/bff/fams/investment-managers/${id}/stage`, {
    method: 'PUT',
    data: { stage },
    ...(options || {}),
  });

export const createInvestmentManagerNote = (
  managerId: number,
  data: CreateInvestmentManagerNotePayload,
  options?: ApiRequestOptions,
) =>
  request<InvestmentManagerNote>(`/bff/fams/investment-managers/${managerId}/notes`, {
    method: 'POST',
    data,
    ...(options || {}),
  });

export const updateInvestmentManagerNote = (
  managerId: number,
  noteId: number,
  data: CreateInvestmentManagerNotePayload,
  options?: ApiRequestOptions,
) =>
  request<InvestmentManagerNote>(
    `/bff/fams/investment-managers/${managerId}/notes/${noteId}`,
    { method: 'PUT', data, ...(options || {}) },
  );

export const deleteInvestmentManagerNote = (
  managerId: number,
  noteId: number,
  options?: ApiRequestOptions,
) =>
  request<{ success: boolean; id: number }>(
    `/bff/fams/investment-managers/${managerId}/notes/${noteId}`,
    { method: 'DELETE', ...(options || {}) },
  );

export const addInvestmentManagerProduct = (
  managerId: number,
  data: { productId: number; roleType?: string; startDate?: string; endDate?: string; isCurrent?: number },
  options?: ApiRequestOptions,
) =>
  request<InvestmentManagerProductRelation[]>(
    `/bff/fams/investment-managers/${managerId}/products`,
    { method: 'POST', data, ...(options || {}) },
  );

export const deleteInvestmentManagerProduct = (
  managerId: number,
  relationId: number,
  options?: ApiRequestOptions,
) =>
  request<{ success: boolean; id: number }>(
    `/bff/fams/investment-managers/${managerId}/products/${relationId}`,
    { method: 'DELETE', ...(options || {}) },
  );

export const getInvestmentManagerPortrait = (id: number, options?: ApiRequestOptions) =>
  request<InvestmentManagerPortrait>(`/bff/fams/investment-managers/${id}/portrait`, {
    method: 'GET', ...(options || {}),
  });

export const updateInvestmentManagerPortrait = (
  id: number,
  data: InvestmentManagerPortrait,
  options?: ApiRequestOptions,
) => request<InvestmentManagerPortrait>(`/bff/fams/investment-managers/${id}/portrait`, {
  method: 'PUT', data, ...(options || {}),
});

export const getInvestmentManagerCareer = (id: number, options?: ApiRequestOptions) =>
  request<InvestmentManagerCareer>(`/bff/fams/investment-managers/${id}/career`, {
    method: 'GET', ...(options || {}),
  });

export const createInvestmentManagerCareerEvent = (
  id: number,
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) => request<InvestmentManagerCareer>(`/bff/fams/investment-managers/${id}/career-events`, {
  method: 'POST', data, ...(options || {}),
});

export const deleteInvestmentManagerCareerEvent = (
  id: number,
  eventId: number,
  options?: ApiRequestOptions,
) => request<{ success: boolean; id: number }>(
  `/bff/fams/investment-managers/${id}/career-events/${eventId}`,
  { method: 'DELETE', ...(options || {}) },
);

export const updateInvestmentManagerCareerEvent = (
  id: number,
  eventId: number,
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) => request<InvestmentManagerCareer>(
  `/bff/fams/investment-managers/${id}/career-events/${eventId}`,
  { method: 'PUT', data, ...(options || {}) },
);

export const saveInvestmentManagerMarketExperience = (
  id: number,
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) => request<InvestmentManagerCareer>(`/bff/fams/investment-managers/${id}/market-experiences`, {
  method: 'POST', data, ...(options || {}),
});

export const getInvestmentManagerOutputs = (id: number, options?: ApiRequestOptions) =>
  request<InvestmentManagerResearchOutput[]>(`/bff/fams/investment-managers/${id}/outputs`, {
    method: 'GET', ...(options || {}),
  });

export const uploadInvestmentManagerOutput = (
  id: number,
  data: FormData,
  options?: ApiRequestOptions,
) => request<InvestmentManagerResearchOutput>(`/bff/fams/investment-managers/${id}/outputs/upload`, {
  method: 'POST', data, ...(options || {}),
});

export const archiveInvestmentManagerOutput = (
  id: number,
  outputId: number,
  options?: ApiRequestOptions,
) => request<{ success: boolean; id: number }>(
  `/bff/fams/investment-managers/${id}/outputs/${outputId}/archive`,
  { method: 'PATCH', ...(options || {}) },
);

export const getInvestmentManagerAnalytics = (
  id: number,
  params?: { asOfDate?: string; methodologyVersion?: string; peerGroup?: string },
  options?: ApiRequestOptions,
) => request<InvestmentManagerAnalytics>(`/bff/fams/investment-managers/${id}/analytics`, {
  method: 'GET', params, ...(options || {}),
});

export const getInvestmentManagerEvaluations = (id: number, options?: ApiRequestOptions) =>
  request<InvestmentManagerEvaluation[]>(`/bff/fams/investment-managers/${id}/evaluations`, {
    method: 'GET', ...(options || {}),
  });

export const saveInvestmentManagerEvaluation = (
  id: number,
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) => request<InvestmentManagerEvaluation>(`/bff/fams/investment-managers/${id}/evaluations`, {
  method: 'POST', data, ...(options || {}),
});

export const publishInvestmentManagerEvaluation = (
  id: number,
  evaluationId: number,
  options?: ApiRequestOptions,
) => request<InvestmentManagerEvaluation>(
  `/bff/fams/investment-managers/${id}/evaluations/${evaluationId}/publish`,
  { method: 'POST', ...(options || {}) },
);

export const getInvestmentManagerReports = (
  id: number,
  options?: ApiRequestOptions,
) => request<InvestmentManagerReportSummary[]>(
  `/bff/fams/investment-managers/${id}/reports`,
  { method: 'GET', ...(options || {}) },
);

export const getInvestmentManagerReport = (
  id: number,
  options?: ApiRequestOptions,
) => request<InvestmentManagerReportDetail>(
  `/bff/fams/investment-manager-reports/${id}`,
  { method: 'GET', ...(options || {}) },
);

export const generateInvestmentManagerReport = (
  data: GenerateInvestmentManagerReportPayload,
  options?: ApiRequestOptions,
) => request<InvestmentManagerReportDetail>(
  '/bff/fams/investment-manager-reports/generate',
  { method: 'POST', data, ...(options || {}) },
);

export const updateInvestmentManagerReport = (
  id: number,
  data: UpdateInvestmentManagerReportPayload,
  options?: ApiRequestOptions,
) => request<InvestmentManagerReportDetail>(
  `/bff/fams/investment-manager-reports/${id}`,
  { method: 'PUT', data, ...(options || {}) },
);

export const publishInvestmentManagerReport = (
  id: number,
  options?: ApiRequestOptions,
) => request<InvestmentManagerReportDetail>(
  `/bff/fams/investment-manager-reports/${id}/publish`,
  { method: 'POST', ...(options || {}) },
);
